import React, { useMemo, useState } from "react";
import {
    Box, Button, IconButton, InputAdornment, List, ListItemButton, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { UNFILED, hasDragItems, readDragItems } from "../helper/projects";
import { ITEM_KIND_STYLES } from "./itemKindStyles";

/** "3 · 1 · 2" and its long form for the tooltip. */
export function countsOf(counts) {
    const explorations = Number(counts?.exploration_count ?? 0);
    const selections = Number(counts?.selection_count ?? 0);
    const reproductions = Number(counts?.reproduction_count ?? 0);
    const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
    return {
        explorations,
        selections,
        reproductions,
        short: `${explorations} · ${selections} · ${reproductions}`,
        long: `${plural(explorations, "exploration")}, ${plural(selections, "selection")}, ${plural(reproductions, "reproduction")}`,
        total: explorations + selections + reproductions,
    };
}

/** The letter tile of a project: its initials on a navy of the chrome
 *  (one colour for all projects). */
export const PROJECT_TILE_COLOR = "#2C3E64";
export function projectTile(name) {
    const words = (name || "").trim().split(/\s+/).filter(Boolean);
    const initials = words.length >= 2
        ? `${words[0][0]}${words[1][0]}`
        : (words[0] || "?").slice(0, 2);
    return { initials: initials.toUpperCase(), color: PROJECT_TILE_COLOR };
}

/** The top bar is 74 px high; the panel stays inside the scrolling main
 *  column with a 16 px margin above and below. */
const STICKY_TOP = 16;
const PANEL_MAX_HEIGHT = `calc(100vh - 74px - ${2 * STICKY_TOP}px)`;

const ACCENT = "#4C7EFF";

const searchFieldSx = {
    "& .MuiOutlinedInput-root": {
        color: "#ffffff",
        bgcolor: "rgba(255,255,255,0.06)",
        borderRadius: 2,
        fontSize: 13,
        "& fieldset": { borderColor: "rgba(255,255,255,0.12)" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,0.25)" },
        "&.Mui-focused fieldset": { borderColor: ACCENT },
    },
    "& input::placeholder": { color: "rgba(255,255,255,0.45)", opacity: 1 },
};

/**
 * The left column of the Projects page: a dark panel, sticky while the cards
 * on the right scroll, with the Unfiled bucket pinned on top and the
 * projects below, each row a drop target for the cards.
 *
 * @param projects      [{ id, name, *_count }]
 * @param unfiled       { *_count } of the items of no project
 * @param activeId      the open project id, or UNFILED
 * @param onSelect      (id) => void
 * @param onDropItems   (projectId | UNFILED, items) => void  — a card drop
 * @param onNew         opens the New project dialog
 * @param onRename      (project) => void, on the open project only
 * @param onDelete      (project) => void, on the open project only
 * @param deleteRightsOf (project) => { allowed, reason }
 */
export default function ProjectList({
    projects, unfiled, activeId, onSelect, onDropItems, onNew, onRename, onDelete, deleteRightsOf,
}) {
    // The row a card drag is hovering over.
    const [dropTarget, setDropTarget] = useState(null);
    const [query, setQuery] = useState("");

    const shown = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (!needle) return projects;
        return projects.filter((project) => project.name.toLowerCase().includes(needle));
    }, [projects, query]);

    const dropHandlers = (targetId) => ({
        onDragOver: (event) => {
            if (!hasDragItems(event.dataTransfer)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            if (dropTarget !== targetId) setDropTarget(targetId);
        },
        onDragLeave: (event) => {
            // Leaving for a child element is not leaving the row.
            if (event.currentTarget.contains(event.relatedTarget)) return;
            setDropTarget((current) => (current === targetId ? null : current));
        },
        onDrop: (event) => {
            const items = readDragItems(event.dataTransfer);
            setDropTarget(null);
            if (items.length === 0) return;
            event.preventDefault();
            onDropItems(targetId, items);
        },
    });

    const rowSx = (targetId, active) => ({
        position: "relative",
        borderRadius: 2.5,
        mb: 0.5,
        px: 1.25,
        py: 1,
        alignItems: "center",
        gap: 1.25,
        transition: "background-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
        "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        "&.Mui-selected": {
            bgcolor: "rgba(76, 126, 255, 0.22)",
            "&:hover": { bgcolor: "rgba(76, 126, 255, 0.28)" },
        },
        // The accent bar of the open project.
        ...(active ? {
            "&::before": {
                content: '""', position: "absolute", left: 0, top: 10, bottom: 10, width: 3,
                borderRadius: 999, bgcolor: ACCENT,
            },
        } : {}),
        ...(dropTarget === targetId ? {
            bgcolor: "rgba(76, 126, 255, 0.32) !important",
            boxShadow: `0 0 0 2px ${ACCENT}, 0 0 18px rgba(76, 126, 255, 0.55)`,
            transform: "scale(1.02)",
        } : {}),
    });

    const renderCounts = (counts, active) => {
        const c = countsOf(counts);
        const groups = [
            ["exploration", c.explorations], ["selection", c.selections], ["reproduction", c.reproductions],
        ];
        return (
            <Stack direction="row" spacing={1.25} alignItems="center" aria-label={c.long} sx={{ mt: 0.35 }}>
                {groups.map(([kind, count]) => {
                    const { Icon } = ITEM_KIND_STYLES[kind];
                    return (
                        <Stack
                            key={kind}
                            direction="row"
                            spacing={0.4}
                            alignItems="center"
                            sx={{ color: count > 0 ? (active ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.62)") : "rgba(255,255,255,0.28)" }}
                        >
                            <Icon sx={{ fontSize: 13 }} />
                            <Typography
                                component="span"
                                sx={{ fontSize: 11.5, fontWeight: 600, lineHeight: 1, fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" }}
                            >
                                {count}
                            </Typography>
                        </Stack>
                    );
                })}
            </Stack>
        );
    };

    const renderRow = ({ id, name, counts, project }) => {
        const active = activeId === id;
        const rights = project && deleteRightsOf ? deleteRightsOf(project) : null;
        const tile = project ? projectTile(name) : null;
        return (
            <ListItemButton
                key={id}
                selected={active}
                onClick={() => onSelect(id)}
                data-project-row={id}
                sx={rowSx(id, active)}
                {...dropHandlers(id)}
            >
                {/* letter tile (dashed folder for Unfiled) */}
                <Box
                    sx={{
                        width: 34, height: 34, borderRadius: 2, flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 12.5, fontWeight: 800, letterSpacing: 0.3, color: "#ffffff",
                        ...(tile
                            ? { bgcolor: tile.color, boxShadow: "inset 0 -2px 0 rgba(0,0,0,0.18)" }
                            : { border: "1.5px dashed rgba(255,255,255,0.35)", color: "rgba(255,255,255,0.6)" }),
                    }}
                    aria-hidden="true"
                >
                    {tile ? tile.initials : <FolderOffOutlinedIcon sx={{ fontSize: 18 }} />}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                        noWrap
                        title={name}
                        sx={{
                            fontSize: 14, lineHeight: 1.25,
                            fontWeight: active ? 700 : 600,
                            color: active ? "#ffffff" : "rgba(255,255,255,0.86)",
                        }}
                    >
                        {name}
                    </Typography>
                    {renderCounts(counts, active)}
                </Box>
                {active && project ? (
                    <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, mr: -0.5 }}>
                        <Tooltip title="Rename project">
                            <IconButton
                                size="small"
                                aria-label={`rename project ${name}`}
                                onClick={(event) => { event.stopPropagation(); onRename(project); }}
                                sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ffffff" } }}
                            >
                                <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={rights?.reason || "Delete project"}>
                            <span>
                                <IconButton
                                    size="small"
                                    aria-label={`delete project ${name}`}
                                    disabled={rights ? !rights.allowed : false}
                                    onClick={(event) => { event.stopPropagation(); onDelete(project); }}
                                    sx={{
                                        color: "rgba(255,255,255,0.7)", "&:hover": { color: "#ff8a80" },
                                        "&.Mui-disabled": { color: "rgba(255,255,255,0.25)" },
                                    }}
                                >
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                ) : null}
            </ListItemButton>
        );
    };

    return (
        <Box
            sx={{
                position: { xs: "static", md: "sticky" },
                top: STICKY_TOP,
                maxHeight: { xs: "none", md: PANEL_MAX_HEIGHT },
                borderRadius: 3,
                // The side menu's navy, so the panel reads as the same chrome.
                background: "linear-gradient(180deg, #1a2230 0%, #202b3d 100%)",
                color: "#ffffff",
                boxShadow: "0 18px 40px rgba(15, 23, 42, 0.22)",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden",
            }}
        >
            <Box sx={{ px: 2.25, pt: 2, pb: 1.5 }}>
                <Typography
                    variant="overline"
                    sx={{ display: "block", color: "rgba(255,255,255,0.45)", fontWeight: 700, letterSpacing: 1.2, lineHeight: 1.6 }}
                >
                    Workspace
                </Typography>
                <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                    <Typography sx={{ fontSize: 19, fontWeight: 800, lineHeight: 1.2 }}>Projects</Typography>
                    <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)" }}>
                        {projects.length} project{projects.length === 1 ? "" : "s"}
                    </Typography>
                </Stack>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Find a project"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    inputProps={{ "aria-label": "Find a project" }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.45)" }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mt: 1.5, ...searchFieldSx }}
                />
            </Box>
            <List
                dense
                aria-label="projects"
                sx={{
                    px: 1, py: 0, flex: 1, minHeight: 0, overflowY: "auto",
                    scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.25) transparent",
                }}
            >
                {renderRow({ id: UNFILED, name: "Unfiled", counts: unfiled, project: null })}
                {shown.length > 0 ? (
                    <Box
                        sx={{
                            mx: 1.25, my: 1, height: "1px",
                            background: "linear-gradient(90deg, rgba(255,255,255,0.18), rgba(255,255,255,0))",
                        }}
                    />
                ) : null}
                {shown.map((project) => renderRow({ id: project.id, name: project.name, counts: project, project }))}
                {projects.length > 0 && shown.length === 0 ? (
                    <Typography sx={{ px: 1.5, py: 1.5, fontSize: 13, color: "rgba(255,255,255,0.5)" }}>
                        No project matches.
                    </Typography>
                ) : null}
            </List>
            <Box sx={{ p: 1.5, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onNew}
                    sx={{
                        textTransform: "none", fontWeight: 700, borderRadius: 2, py: 0.9,
                        bgcolor: ACCENT, "&:hover": { bgcolor: "#3b6ae6" },
                    }}
                >
                    New project
                </Button>
            </Box>
        </Box>
    );
}
